import React, { useEffect, useMemo, useState } from "react";
import { Circle, CircleMarker, MapContainer, Marker, Pane, Polyline, Popup, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
  HOTEL_COLOR,
  PICKUP_COLOR,
  ageLabel,
  formatClock,
  formatMinutes,
  minutesBetween,
  routeArrows,
} from "./tripMapModel";
import { formatDateTimeDMY12 } from "../../utils/dateFormat";
import "./tripMap.css";

// Where the map opens before it has fitted itself to the trip.
const DEFAULT_CENTER = [13.3, 75.8];
const PIN_PATH = "M0,0 C-2.5,-6 -11,-11.5 -11,-20.5 A11,11 0 1 1 11,-20.5 C11,-11.5 2.5,-6 0,0 Z";
const BED_ICON = '<g transform="translate(-8,-29) scale(0.667)" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v12M3 15h18v4M21 15v-3a3 3 0 0 0-3-3h-7v6"/><circle cx="7" cy="11" r="1.6"/></g>';
const iconCache = new Map();

const STATUS_TEXT = { visited: "Visited", here: "Here now", upcoming: "Upcoming", missed: "Not visited" };
const MISSED_COLOR = "#94a3b8";

/**
 * The trip on a map: a pin per place (colour = day, fill = progress), the night stays, the arrival
 * circle round each, the route driven and the vehicle. dayFilter is "all" or a day index.
 * officeView adds what only the office sees: "Not visited" on missed places, and the unplanned
 * stops passed in {@code stops}.
 */
export default function TripMap({
  model,
  dayFilter = "all",
  showArrival = true,
  showLeaveRing = false,
  showHotels = true,
  showRawPoints = false,
  routeSinceMinutes = null,
  fitPadding = { topLeft: [36, 36], bottomRight: [36, 36] },
  controlsStyle,
  scrollWheelZoom = false,
  officeView = false,
  stops = [],
  className = "",
  children,
}) {
  const [map, setMap] = useState(null);
  const view = useMemo(
    () => visibleContent(model, dayFilter, routeSinceMinutes),
    [model, dayFilter, routeSinceMinutes],
  );

  // Fit once per day choice, and again only when pins appear - never on the minute-by-minute
  // refresh, which would throw away wherever the viewer has panned to.
  const fitKey = `${dayFilter}|${view.places.length}|${view.hotels.length}|${view.pickup ? 1 : 0}|${model?.vehicle ? 1 : 0}`;
  useEffect(() => {
    if (!map) return;
    // Inside a window that is still opening, Leaflet measured the box before it had its size.
    map.invalidateSize();
    fitTo(map, view.bounds, fitPadding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey]);

  const zoomBy = (delta) => map && map.setZoom(map.getZoom() + delta);
  const followVehicle = () => {
    if (map && model?.vehicle) map.setView([model.vehicle.lat, model.vehicle.lng], Math.max(map.getZoom(), 14));
  };

  return (
    <div className={`trip-map ${className}`}>
      <MapContainer
        ref={setMap}
        center={DEFAULT_CENTER}
        zoom={9}
        zoomControl={false}
        scrollWheelZoom={scrollWheelZoom}
        className="trip-map-leaflet"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {view.trail.map((line) => (
          <React.Fragment key={`trail-${line.dayIndex}`}>
            <Polyline positions={line.points} pathOptions={{ color: "#ffffff", weight: 8, opacity: 0.9 }} />
            <Polyline positions={line.points} pathOptions={{ color: line.color, weight: 4.5, opacity: 1 }} />
          </React.Fragment>
        ))}
        {/* Above the lines and circles, below every pin. */}
        <Pane name="trip-route-arrows" style={{ zIndex: 450 }}>
          <RouteArrows lines={view.trail} />
        </Pane>
        {view.planned.map((segment) => (
          <Polyline
            key={segment.key}
            positions={segment.points}
            pathOptions={{ color: segment.color, weight: 4, opacity: 0.75, dashArray: "1 9", lineCap: "round" }}
          />
        ))}
        {showLeaveRing && view.places.map((stop) => (
          <Circle
            key={`leave-${stop.key}`}
            center={[stop.lat, stop.lng]}
            radius={stop.leaveRadiusM}
            pathOptions={{ color: stop.color, weight: 1.2, opacity: 0.45, fill: false, dashArray: "1 6", lineCap: "round" }}
          />
        ))}
        {showArrival && view.places.map((stop) => (
          <Circle
            key={`arrive-${stop.key}`}
            center={[stop.lat, stop.lng]}
            radius={stop.arrivalRadiusM}
            pathOptions={{
              color: stop.color,
              weight: 1.4,
              opacity: 0.7,
              dashArray: "4 4",
              fillColor: stop.color,
              fillOpacity: stop.status === "here" ? 0.16 : 0.1,
            }}
          />
        ))}
        {showArrival && showHotels && view.hotels.map((hotel) => (
          <Circle
            key={`arrive-${hotel.key}`}
            center={[hotel.lat, hotel.lng]}
            radius={model.arrivalRadiusM}
            pathOptions={{ color: HOTEL_COLOR, weight: 1.4, opacity: 0.55, dashArray: "4 4", fillColor: HOTEL_COLOR, fillOpacity: 0.08 }}
          />
        ))}
        {showArrival && view.pickup ? (
          <Circle
            center={[view.pickup.lat, view.pickup.lng]}
            radius={model.arrivalRadiusM}
            pathOptions={{ color: PICKUP_COLOR, weight: 1.4, opacity: 0.6, dashArray: "4 4", fillColor: PICKUP_COLOR, fillOpacity: 0.08 }}
          />
        ) : null}
        {showRawPoints && view.rawPoints.map((point, index) => (
          <CircleMarker
            key={point.id || point.clientPingId || index}
            center={[point.lat, point.lng]}
            radius={3}
            pathOptions={{ color: "#ffffff", weight: 1, fillColor: "#334155", fillOpacity: 0.85 }}
          >
            <Popup>
              {formatClock(point.capturedAtIst)}
              {point.accuracy ? <><br />Accuracy: {Math.round(point.accuracy)} m</> : null}
            </Popup>
          </CircleMarker>
        ))}
        {showHotels && view.hotels.map((hotel) => (
          <Marker key={hotel.key} position={[hotel.lat, hotel.lng]} icon={hotelIcon()} zIndexOffset={100}>
            {/* Leaflet fixes "permanent" when a tooltip is made, so switching it means a new one. */}
            <Tooltip key={view.permanentLabels ? "shown" : "hover"} permanent={view.permanentLabels} direction="bottom" offset={[0, 2]} className="trip-map-label">
              {hotel.nightLabel}
            </Tooltip>
            <Popup>
              <div className="trip-map-popup">
                <div className="trip-map-popup-title">{hotel.name}</div>
                <div>{hotel.nightLabel}</div>
                {hotel.caretakerNumber ? (
                  <div>
                    Caretaker: {hotel.caretakerName ? `${hotel.caretakerName} - ` : ""}
                    <a href={`tel:${hotel.caretakerNumber.replace(/[^\d+]/g, "")}`}>{hotel.caretakerNumber}</a>
                  </div>
                ) : null}
                {hotel.mapLink ? <a href={hotel.mapLink} target="_blank" rel="noopener noreferrer">Open in Google Maps</a> : null}
              </div>
            </Popup>
          </Marker>
        ))}
        {view.pickup ? (
          <Marker position={[view.pickup.lat, view.pickup.lng]} icon={pickupIcon()} zIndexOffset={150}>
            <Tooltip key={view.permanentLabels ? "shown" : "hover"} permanent={view.permanentLabels} direction="bottom" offset={[0, 2]} className="trip-map-label">
              Pickup
            </Tooltip>
            <Popup>
              <div className="trip-map-popup">
                <div className="trip-map-popup-title">Pickup point</div>
                {view.pickup.landmark ? <div>{view.pickup.landmark}</div> : null}
                {view.pickup.time ? <div>{formatDateTimeDMY12(view.pickup.time, "")}</div> : null}
                {view.pickup.reached ? <div>Driver reached {formatClock(view.pickup.reached.capturedAtIst)}</div> : null}
                {view.pickup.contactPhone ? (
                  <div>Call: <a href={`tel:${view.pickup.contactPhone}`}>{view.pickup.contactPhone}</a></div>
                ) : null}
                {view.pickup.mapLink ? <a href={view.pickup.mapLink} target="_blank" rel="noopener noreferrer">Open in Google Maps</a> : null}
              </div>
            </Popup>
          </Marker>
        ) : null}
        {view.places.map((stop) => (
          <Marker
            key={stop.key}
            position={[stop.lat, stop.lng]}
            icon={pinIcon(stop)}
            zIndexOffset={stop.status === "here" ? 400 : stop.status === "upcoming" ? 200 : 300}
          >
            <Tooltip key={view.permanentLabels ? "shown" : "hover"} permanent={view.permanentLabels} direction="bottom" offset={[0, 2]} className="trip-map-label">
              {stop.label}
            </Tooltip>
            <Popup>
              <PlacePopup stop={stop} officeView={officeView} />
            </Popup>
          </Marker>
        ))}
        {officeView && stops.map((stop) => (
          <Marker key={`stop-${stop.fromIst}`} position={[stop.latitude, stop.longitude]} icon={unplannedStopIcon()} zIndexOffset={120}>
            <Tooltip direction="right" offset={[10, 0]} className="trip-map-label">
              Stopped {formatMinutes(stop.minutes)}
            </Tooltip>
            <Popup>
              <div className="trip-map-popup">
                <div className="trip-map-popup-title">Unplanned stop · {formatMinutes(stop.minutes)}</div>
                <div>{formatDateTimeDMY12(stop.fromIst, "")} to {formatClock(stop.toIst)}</div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${stop.latitude},${stop.longitude}`} target="_blank" rel="noopener noreferrer">
                  Open in Google Maps
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
        {view.vehicle ? (
          <Marker position={[view.vehicle.lat, view.vehicle.lng]} icon={vehicleIcon(view.vehicle)} zIndexOffset={600}>
            {view.vehicle.stale ? (
              <Tooltip permanent direction="right" offset={[16, 0]} className="trip-map-label trip-map-label-stale">
                Last seen {ageLabel(view.vehicle.minutesAgo)}
              </Tooltip>
            ) : null}
            <Popup>
              <div className="trip-map-popup">
                <div className="trip-map-popup-title">Vehicle</div>
                <div>Updated {formatClock(view.vehicle.capturedAtIst)} ({ageLabel(view.vehicle.minutesAgo)})</div>
              </div>
            </Popup>
          </Marker>
        ) : null}
      </MapContainer>

      <div className="trip-map-controls" style={controlsStyle}>
        <div className="trip-map-btn-group">
          <button type="button" className="trip-map-btn" aria-label="Zoom in" onClick={() => zoomBy(1)}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
          </button>
          <button type="button" className="trip-map-btn" aria-label="Zoom out" onClick={() => zoomBy(-1)}>
            <i className="bi bi-dash-lg" aria-hidden="true" />
          </button>
        </div>
        <button
          type="button"
          className="trip-map-btn trip-map-btn-single"
          aria-label="Show everything"
          onClick={() => map && fitTo(map, view.bounds, fitPadding)}
        >
          <i className="bi bi-bullseye" aria-hidden="true" />
        </button>
        {model?.vehicle ? (
          <button type="button" className="trip-map-btn trip-map-btn-single trip-map-btn-accent" aria-label="Go to the vehicle" onClick={followVehicle}>
            <i className="bi bi-cursor-fill" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/**
 * Arrows along the driven route showing which way the vehicle went, worked out again after each
 * zoom or pan so they stay evenly spaced on screen. Only the part of the route in view (and a
 * screen around it) gets arrows, so a long trip zoomed in close stays light.
 */
function RouteArrows({ lines }) {
  const map = useMap();
  const [viewKey, setViewKey] = useState(0);
  useMapEvents({ moveend: () => setViewKey((key) => key + 1) });

  const arrows = useMemo(() => {
    const zoom = map.getZoom();
    const shown = map.getPixelBounds();
    const margin = shown.getSize();
    const inReach = (point) => point.x >= shown.min.x - margin.x && point.x <= shown.max.x + margin.x
      && point.y >= shown.min.y - margin.y && point.y <= shown.max.y + margin.y;
    return lines.flatMap((line) => {
      const pixels = line.points.map((point) => map.project(point, zoom));
      return routeArrows(pixels)
        .filter(inReach)
        .map((arrow, index) => ({
          key: `${line.dayIndex}-${index}`,
          position: map.unproject([arrow.x, arrow.y], zoom),
          icon: arrowIcon(line.color, arrow.angle),
        }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, lines, viewKey]);

  return arrows.map((arrow) => (
    <Marker key={arrow.key} position={arrow.position} icon={arrow.icon} interactive={false} keyboard={false} />
  ));
}

function PlacePopup({ stop, officeView }) {
  const spent = minutesBetween(stop.dropped?.capturedAtIst, stop.pickedUp?.capturedAtIst);
  return (
    <div className="trip-map-popup">
      <div className="trip-map-popup-title">{stop.number ? `${stop.number}. ` : ""}{stop.label}</div>
      {stop.status !== "missed" || officeView ? <div>{STATUS_TEXT[stop.status]}{officeView && stop.auto ? " · marked automatically" : ""}</div> : null}
      {stop.dropped ? <div>Reached {formatClock(stop.dropped.capturedAtIst)}</div> : null}
      {stop.pickedUp ? <div>Left {formatClock(stop.pickedUp.capturedAtIst)}{spent != null ? ` · ${formatMinutes(spent)}` : ""}</div> : null}
      {stop.mapLink ? <a href={stop.mapLink} target="_blank" rel="noopener noreferrer">Open in Google Maps</a> : null}
    </div>
  );
}

function visibleContent(model, dayFilter, routeSinceMinutes) {
  const empty = { places: [], hotels: [], pickup: null, trail: [], planned: [], rawPoints: [], vehicle: null, bounds: [], permanentLabels: false };
  if (!model) return empty;
  const all = dayFilter === "all";
  const days = all ? model.days : model.days.filter((day) => day.index === dayFilter);

  const places = days.flatMap((day) => day.places.filter((stop) => stop.hasPoint));
  const hotelsByKey = new Map();
  (all ? model.hotels : days.flatMap((day) => [...day.startStay, ...day.tonight]))
    .filter((hotel) => hotel.hasPoint)
    .forEach((hotel) => hotelsByKey.set(hotel.key, hotel));
  const hotels = Array.from(hotelsByKey.values());

  // The office can trim a long trail back to the last few hours.
  const since = routeSinceMinutes ? Date.now() - routeSinceMinutes * 60000 : null;
  const inWindow = (time) => !since || (time || 0) >= since;
  const trail = days
    .map((day) => ({
      dayIndex: day.index,
      color: day.color,
      points: (model.trail[day.index] || []).filter((point) => inWindow(point.at)).map((point) => [point.lat, point.lng]),
    }))
    .filter((line) => line.points.length > 1);

  const planned = [];
  if (!model.tripEnded) {
    days.forEach((day) => {
      if (day.index < model.currentDayIndex) return;
      const start = day.startStay.find((hotel) => hotel.hasPoint);
      const tonight = day.tonight.find((hotel) => hotel.hasPoint);
      const sequence = [
        ...(start ? [{ lat: start.lat, lng: start.lng, upcoming: false }] : []),
        ...day.places.filter((stop) => stop.hasPoint).map((stop) => ({ lat: stop.lat, lng: stop.lng, upcoming: stop.status === "upcoming" })),
        ...(tonight ? [{ lat: tonight.lat, lng: tonight.lng, upcoming: day.visitedCount < day.places.length || day.index > model.currentDayIndex }] : []),
      ];
      for (let index = 1; index < sequence.length; index += 1) {
        if (!sequence[index].upcoming) continue;
        planned.push({
          key: `plan-${day.index}-${index}`,
          color: day.color,
          points: [[sequence[index - 1].lat, sequence[index - 1].lng], [sequence[index].lat, sequence[index].lng]],
        });
      }
    });
  }

  const dayIndexes = new Set(days.map((day) => day.index));
  const rawPoints = model.rawPoints.filter((point) => inWindow(point.at));
  const vehicle = model.vehicle && (all || dayIndexes.has(model.currentDayIndex)) ? model.vehicle : null;
  // The pickup belongs to Day 1, even when an extra night puts it before the package starts.
  const pickup = model.pickup && (all || dayIndexes.has(0)) ? model.pickup : null;
  const bounds = [
    ...places.map((stop) => [stop.lat, stop.lng]),
    ...hotels.map((hotel) => [hotel.lat, hotel.lng]),
    ...(pickup ? [[pickup.lat, pickup.lng]] : []),
    ...trail.flatMap((line) => line.points),
    ...(vehicle ? [[vehicle.lat, vehicle.lng]] : []),
  ];
  return { places, hotels, pickup, trail, planned, rawPoints, vehicle, bounds, permanentLabels: !all };
}

function fitTo(map, bounds, padding) {
  if (!bounds.length) return;
  if (bounds.length === 1) {
    map.setView(bounds[0], 14);
    return;
  }
  map.fitBounds(bounds, { paddingTopLeft: padding.topLeft, paddingBottomRight: padding.bottomRight, maxZoom: 15 });
}

function pinIcon(stop) {
  const key = `pin|${stop.color}|${stop.status}|${stop.number ?? ""}`;
  if (!iconCache.has(key)) {
    const filled = stop.status === "visited" || stop.status === "here";
    const color = stop.status === "missed" ? MISSED_COLOR : stop.color;
    const body = `<path d="${PIN_PATH}" fill="${filled ? color : "#fff"}" stroke="${filled ? "#fff" : color}" stroke-width="${filled ? 1.5 : 2.2}"/>`;
    const inner = stop.status === "visited"
      ? '<path d="M-4.6,-20.5 L-1.4,-17.3 L4.8,-23.7" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>'
      : `<text x="0" y="-16.4" text-anchor="middle" font-family="system-ui, sans-serif" font-size="12" font-weight="800" fill="${filled ? "#fff" : color}">${stop.number ?? ""}</text>`;
    const pulse = stop.status === "here" ? `<span class="trip-pin-pulse" style="background:${stop.color}"></span>` : "";
    iconCache.set(key, L.divIcon({
      html: `<div class="trip-pin">${pulse}<svg width="30" height="41" viewBox="-13 -35 26 36" aria-hidden="true"><ellipse rx="5" ry="2" fill="#0f1f33" fill-opacity=".22"/>${body}${inner}</svg></div>`,
      className: "trip-map-icon",
      iconSize: [30, 41],
      iconAnchor: [15, 40],
      popupAnchor: [0, -38],
      tooltipAnchor: [0, 0],
    }));
  }
  return iconCache.get(key);
}

// An arrowhead in the day's colour with a white edge, so it reads on the line and off it.
function arrowIcon(color, angle) {
  const turn = ((Math.round(angle / 5) * 5) % 360 + 360) % 360;
  const key = `arrow|${color}|${turn}`;
  if (!iconCache.has(key)) {
    iconCache.set(key, L.divIcon({
      html: `<svg width="22" height="22" viewBox="-11 -11 22 22" aria-hidden="true"><path d="M-6.5,-7 L7.5,0 L-6.5,7 L-3,0 Z" fill="${color}" stroke="#fff" stroke-width="1.8" stroke-linejoin="round" transform="rotate(${turn})"/></svg>`,
      className: "trip-map-icon",
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    }));
  }
  return iconCache.get(key);
}

function unplannedStopIcon() {
  if (!iconCache.has("unplanned-stop")) {
    iconCache.set("unplanned-stop", L.divIcon({
      html: '<div class="trip-unplanned-stop"><i class="bi bi-pause-fill"></i></div>',
      className: "trip-map-icon",
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -10],
      tooltipAnchor: [0, 0],
    }));
  }
  return iconCache.get("unplanned-stop");
}

// A green pin with a "P": also the draggable pin of the customer's pickup chooser.
export function pickupIcon() {
  if (!iconCache.has("pickup")) {
    iconCache.set("pickup", L.divIcon({
      html: `<div class="trip-pin"><svg width="30" height="41" viewBox="-13 -35 26 36" aria-hidden="true"><ellipse rx="5" ry="2" fill="#0f1f33" fill-opacity=".22"/><path d="${PIN_PATH}" fill="${PICKUP_COLOR}" stroke="#fff" stroke-width="1.5"/><text x="0" y="-16.2" text-anchor="middle" font-family="system-ui, sans-serif" font-size="12.5" font-weight="900" fill="#fff">P</text></svg></div>`,
      className: "trip-map-icon",
      iconSize: [30, 41],
      iconAnchor: [15, 40],
      popupAnchor: [0, -38],
      tooltipAnchor: [0, 0],
    }));
  }
  return iconCache.get("pickup");
}

function hotelIcon() {
  if (!iconCache.has("hotel")) {
    iconCache.set("hotel", L.divIcon({
      html: `<svg width="34" height="38" viewBox="-17 -36 34 38" aria-hidden="true"><ellipse rx="5" ry="2" fill="#0f1f33" fill-opacity=".22"/><path d="M0,0 L-5,-8 H-12 A3,3 0 0 1 -15,-11 V-31 A3,3 0 0 1 -12,-34 H12 A3,3 0 0 1 15,-31 V-11 A3,3 0 0 1 12,-8 H5 Z" fill="${HOTEL_COLOR}" stroke="#fff" stroke-width="1.5"/>${BED_ICON}</svg>`,
      className: "trip-map-icon",
      iconSize: [34, 38],
      iconAnchor: [17, 36],
      popupAnchor: [0, -34],
      tooltipAnchor: [0, 0],
    }));
  }
  return iconCache.get("hotel");
}

function vehicleIcon(vehicle) {
  const heading = vehicle.heading != null && vehicle.heading >= 0 && !vehicle.stale ? Math.round(vehicle.heading / 10) * 10 : null;
  const key = `vehicle|${vehicle.stale ? 1 : 0}|${heading ?? "-"}`;
  if (!iconCache.has(key)) {
    const color = vehicle.stale ? "#94a3b8" : HOTEL_COLOR;
    const halo = vehicle.stale
      ? '<circle r="19" fill="none" stroke="#64748b" stroke-width="1.3" stroke-dasharray="3 3"/>'
      : `<circle r="19" fill="${color}" fill-opacity=".14"/>`;
    const mark = heading == null
      ? '<circle r="3.6" fill="#fff"/>'
      : `<path d="M0,-5.5 L4.6,4.5 L0,2.2 L-4.6,4.5 Z" fill="#fff" transform="rotate(${heading})"/>`;
    iconCache.set(key, L.divIcon({
      html: `<svg width="44" height="44" viewBox="-22 -22 44 44" aria-hidden="true">${halo}<circle r="11" fill="${color}" stroke="#fff" stroke-width="2.5"/>${mark}</svg>`,
      className: "trip-map-icon",
      iconSize: [44, 44],
      iconAnchor: [22, 22],
      popupAnchor: [0, -14],
    }));
  }
  return iconCache.get(key);
}
